import { useEffect, useMemo, useState } from 'react'
import { shortDate, type LastTime } from '../../lib/api'
import { useDrawerParam, useHashParam } from '../../lib/hashParams'
import {
  REASON_WORDS,
  firstView,
  inView,
  isView,
  reviewViews,
  sectionsOf,
  type ReviewRow,
  type ReviewView,
} from '../../lib/answerReview'
import { CompareTable, Drawer, Status, type CompareColumn } from '../kit'
import { statusWord } from '../../lib/status'
import '../../styles/answer-table.css'

// The answers of one assessment as a table (release 3.3, the mentor's point
// 7). The rules for each row are in lib/answerReview.ts; this draws them.
//
// What the address holds (Back model, 7 Oct plan B5 and B7): the filter as
// ?view= (replaced, so Back does not step through filter clicks) and the open
// row as ?q= (pushed, so Back closes the drawer and a link opens it).
//
// The drawer is the kit's Drawer as main has it today. The architect's open
// dialog work (PR 38) turns every drawer into its Dialog; when it lands this
// one follows it, with the same footer.

// Where an answer came from is shown in its cell unless the vendor attested
// it (the ordinary case) or nothing was answered: an internal, carried or
// extracted answer must never look like one a vendor signed (CLAUDE.md,
// "provenance is per answer").
function sourceTag(r: ReviewRow): string {
  if (r.source.word === 'Attested' || r.source.word === 'Unanswered') return ''
  return r.source.word + (r.source.note ? ', ' + r.source.note : '')
}

function sectionSummary(asked: number, answered: number, needing: number): string {
  return answered + ' of ' + asked + ' answered' + (needing > 0 ? ', ' + needing + ' to review' : '')
}

// What the page knows about the vendor's files: read, still loading, failed
// to load, or not this person's to read. Only "known" draws the Evidence
// column; the drawer says which of the others it is, never "none".
export type EvidenceState = 'known' | 'loading' | 'failed' | 'denied'

export const EVIDENCE_UNKNOWN: Record<Exclude<EvidenceState, 'known'>, string> = {
  loading: 'Loading...',
  failed: 'Could not be read',
  denied: 'Not shown to your role',
}

export function AnswerTable({
  rows,
  lastTime,
  canAsk,
  evidence,
  onAsk,
}: {
  rows: ReviewRow[]
  lastTime?: LastTime
  canAsk: boolean
  evidence: EvidenceState
  onAsk: (questionKey: string) => void
}) {
  const evidenceKnown = evidence === 'known'
  const [viewParam, setView] = useHashParam('view', 'replace')
  // The open row: pushed, so Back closes it; Previous and Next replace.
  const drawer = useDrawerParam('q')
  const openParam = drawer.value
  const view: ReviewView = isView(viewParam) ? viewParam : firstView(rows)
  const views = useMemo(() => reviewViews(rows, Boolean(lastTime)), [rows, lastTime])
  const sections = useMemo(() => sectionsOf(rows, view), [rows, view])

  // Under All, a section with nothing to review starts folded, so the table
  // opens on what needs somebody; under a narrower filter every section that
  // has a row is open. A fold the person changes stays until the filter does.
  const foldKey = view === 'all' ? sections.filter((s) => s.needing === 0).map((s) => s.key).join('|') : ''
  const [folds, setFolds] = useState<Set<string>>(() => new Set(foldKey ? foldKey.split('|') : []))
  useEffect(() => setFolds(new Set(foldKey ? foldKey.split('|') : [])), [foldKey])

  const shown = useMemo(() => rows.filter((r) => inView(r, view)), [rows, view])
  const open = openParam ? (rows.find((r) => r.key === openParam) ?? null) : null
  // The last row shown stays in the drawer while it slides away.
  const [lastOpen, setLastOpen] = useState<ReviewRow | null>(open)
  useEffect(() => {
    if (open) setLastOpen(open)
  }, [open])
  const inDrawer = open ?? lastOpen

  // Closing returns focus to the row the drawer was last on (after Next, not
  // the one first opened), once the drawer has let go of it.
  const closeDrawer = () => {
    const key = open?.key
    return drawer.close().then(() => {
      if (!key) return
      const button = document.querySelector<HTMLElement>('tr[data-row="' + CSS.escape(key) + '"] .ct-open')
      button?.focus()
    })
  }
  const at = open ? shown.findIndex((r) => r.key === open.key) : -1

  const columns: CompareColumn<ReviewRow>[] = [
    { key: 'q', label: 'Question', className: 'ar-q', clamp: true, cell: (r) => r.question.prompt },
    {
      key: 'a',
      label: 'Answer',
      className: 'ar-a',
      clamp: true,
      cell: (r) => (
        <>
          <span className={r.shown.muted ? 'ct-muted' : undefined}>{r.shown.text}</span>
          {sourceTag(r) && <span className="ar-source">{sourceTag(r)}</span>}
        </>
      ),
    },
  ]
  if (lastTime) {
    columns.push({
      key: 'last',
      label: 'Last time',
      className: 'ar-last',
      clamp: true,
      info:
        'The answer in ' +
        lastTime.reference +
        (lastTime.finishedOn ? ', finished ' + shortDate(lastTime.finishedOn) : '') +
        ': the last questionnaire this vendor finished before this one was sent. Matched by question key.',
      cell: (r) => <span className={r.last?.muted ? 'ct-muted' : undefined}>{r.last?.text ?? ''}</span>,
    })
  }
  if (evidenceKnown)
    columns.push({
      key: 'ev',
      label: 'Evidence',
      className: 'ar-ev',
      clamp: true,
      cell: (r) =>
        r.files.length > 0 ? (
          <span title={r.files.join(', ')}>
            {r.files[0]}
            {r.files.length > 1 && ' +' + (r.files.length - 1)}
          </span>
        ) : r.evidenceGap ? (
          <span>None</span>
        ) : (
          <span className="ct-muted">-</span>
        ),
    })
  columns.push(
    {
      key: 'st',
      label: 'Status',
      className: 'ar-st',
      info: 'One word for each answer: a finding this round raised on it, a question we asked about it, a missing or changed answer, missing evidence, or where the answer came from.',
      cell: (r) => (
        <Status tone={r.status.tone} title={r.status.title}>
          {r.status.word}
        </Status>
      ),
    },
    {
      key: 'act',
      label: 'Actions',
      hideLabel: true,
      className: 'ar-act',
      cell: (r) =>
        canAsk && r.asked ? (
          <button type="button" className="ar-ask" onClick={() => onAsk(r.key)} aria-label={'Ask about the answer to ' + r.key}>
            Ask
          </button>
        ) : null,
    },
  )

  const empty =
    view === 'review' ? 'Nothing needs review: every required question is answered, nothing was raised and nothing changed.' : 'No answers here.'

  return (
    <>
      <CompareTable<ReviewRow, ReviewView>
        label="Answers"
        columns={columns}
        groups={sections.map((s) => ({ key: s.key, title: s.title, summary: sectionSummary(s.asked, s.answered, s.needing), rows: s.rows }))}
        rowKey={(r) => r.key}
        itemLabel={(r) => ({ key: r.key, label: 'Open ' + r.key + ': ' + r.question.prompt })}
        onOpen={(r) => drawer.open(r.key)}
        openKey={open?.key ?? null}
        rowClass={(r) => (r.asked ? undefined : 'ar-skipped')}
        filters={views}
        filter={view}
        onFilter={(v) => setView(v === firstView(rows) ? null : v)}
        folded={folds}
        onFold={(key, fold) =>
          setFolds((f) => {
            const next = new Set(f)
            if (fold) next.add(key)
            else next.delete(key)
            return next
          })
        }
        empty={empty}
        maxHeight="max(360px, calc(100vh - 150px))"
      />

      <Drawer
        open={open !== null}
        onClose={() => void closeDrawer()}
        title={inDrawer ? 'Question ' + inDrawer.key : 'Question'}
        footer={
          open && (
            <div className="ar-foot">
              {canAsk && open.asked && (
                <button
                  type="button"
                  className="primary"
                  onClick={() => {
                    // The drawer closes first and focus goes back to its row;
                    // then the question form opens and takes focus.
                    const key = open.key
                    void closeDrawer().then(() => onAsk(key))
                  }}
                >
                  Ask about this answer
                </button>
              )}
              <span className="ar-grow" />
              {at >= 0 && (
                <span className="ar-pos">
                  {at + 1} of {shown.length}
                </span>
              )}
              <button type="button" disabled={at <= 0} onClick={() => at > 0 && drawer.move(shown[at - 1]!.key)}>
                Previous
              </button>
              <button
                type="button"
                disabled={at < 0 || at >= shown.length - 1}
                onClick={() => at >= 0 && at < shown.length - 1 && drawer.move(shown[at + 1]!.key)}
              >
                Next
              </button>
            </div>
          )
        }
      >
        {inDrawer && <RowDetail row={inDrawer} lastTime={lastTime} evidence={evidence} />}
      </Drawer>
    </>
  )
}

function RowDetail({ row, lastTime, evidence }: { row: ReviewRow; lastTime?: LastTime; evidence: EvidenceState }) {
  const help = row.question.helpText.trim()
  const prov = row.source
  const note = row.source.note
  return (
    <div className="prose-drawer-stack ar-detail">
      <div className="prose-chips">
        <Status tone={row.status.tone}>{row.status.word}</Status>
        <span className="prose-mono">{row.question.sectionTitle}</span>
      </div>
      <p className="prose-quote">{row.question.prompt}</p>
      {help && (
        <div>
          <h3>Help for the vendor</h3>
          <p>{help}</p>
        </div>
      )}
      {row.reasons.length > 0 && (
        <div>
          <h3>Why it needs review</h3>
          <ul className="ar-reasons">
            {row.reasons.map((r) => (
              <li key={r}>{REASON_WORDS[r]}</li>
            ))}
          </ul>
        </div>
      )}
      {!row.asked && row.skippedWhy && <p className="prose-sub">{row.skippedWhy}</p>}
      <dl className="prose-kv">
        <dt>Answer</dt>
        <dd>
          <span className={row.shown.muted ? 'ct-muted' : undefined}>{row.shown.text}</span>{' '}
          <Status tone={prov.tone}>{prov.word}</Status>
          {note && <span className="prose-sub"> {note}</span>}
        </dd>
        {lastTime && (
          <>
            <dt>Last time</dt>
            <dd>
              <span className={row.last?.muted ? 'ct-muted' : undefined}>{row.last?.text}</span>
              <span className="prose-sub">
                {' '}
                in {lastTime.reference}
                {lastTime.finishedOn ? ', ' + shortDate(lastTime.finishedOn) : ''}
              </span>
            </dd>
          </>
        )}
        <dt>Evidence</dt>
        <dd>
          {evidence !== 'known'
            ? EVIDENCE_UNKNOWN[evidence]
            : row.files.length > 0
              ? row.files.join(', ')
              : row.question.evidenceRequired
                ? 'None attached (asked for)'
                : 'Not asked for'}
        </dd>
        {row.flags.length > 0 && (
          <>
            <dt>Raised</dt>
            <dd>
              <ul className="ar-reasons">
                {row.flags.map((f) => (
                  <li key={f.kind + f.key}>
                    {f.kind === 'GATE' ? <span className="prose-mono">{f.key}</span> : 'Finding'}
                    {f.title ? ': ' + f.title : ''}
                  </li>
                ))}
              </ul>
            </dd>
          </>
        )}
        {row.clarification && (
          <>
            <dt>Asked</dt>
            <dd>
              Round {row.clarification.roundNo}, {statusWord('clarification', row.clarification.status).word.toLowerCase()}
            </dd>
          </>
        )}
      </dl>
      {row.question.citations.length > 0 && (
        <div>
          <h3>Citations</h3>
          <div className="prose-chips">
            {row.question.citations.map((c) => (
              <span className="prose-cite" key={c}>
                {c}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
